import { LitElement, html, css } from 'lit';
import userSettingStore from './UserSettingStore.js';

export class StarterTemplateModal extends LitElement {
    static styles = css`
        :host {
            display: none;
            position: fixed;
            top: 0; left: 0; width: 100vw; height: 100vh;
            z-index: 100000;
            pointer-events: auto;
        }
        :host([visible]) {
            display: block;
        }
        .overlay {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            display: flex;
            justify-content: center;
            align-items: center;
            animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        
        .modal {
            background: #ffffff;
            width: 90vw;
            max-width: 800px;
            max-height: 90vh;
            max-height: 90dvh; /* iOS Safari用 */
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.5);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            animation: slideUp 0.3s ease-out;
            position: relative;
        }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        .header {
            padding: 24px 30px 10px;
            text-align: center;
        }
        .header h2 { margin: 0; font-size: 1.8rem; color: #111827; }
        .header p { margin: 10px 0 0; color: #6b7280; font-size: 1rem; }

        .cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 20px;
            padding: 20px 30px;
            overflow-y: auto;
            flex-grow: 1;
        }

        .card {
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 24px 20px;
            text-align: center;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            background: #fff;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        .card:hover {
            border-color: #3b82f6;
            box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.3);
            transform: translateY(-4px);
        }
        .card.ai-card {
            background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
            border-color: #bfdbfe;
        }
        .card.ai-card:hover {
            border-color: #60a5fa;
            box-shadow: 0 10px 25px -5px rgba(96, 165, 250, 0.4);
        }

        .icon-circle {
            width: 64px; height: 64px;
            border-radius: 50%;
            background: #f3f4f6;
            color: #4b5563;
            display: flex; justify-content: center; align-items: center;
            font-size: 28px;
            margin-bottom: 16px;
        }
        .ai-card .icon-circle {
            background: #3b82f6; color: white;
        }

        .card h3 { margin: 0 0 8px 0; font-size: 1.2rem; color: #1f2937; }
        .card p { margin: 0; font-size: 0.9rem; color: #6b7280; line-height: 1.5; }

        .footer {
            padding: 16px 30px 24px;
            text-align: center;
        }
        .btn-skip {
            background: transparent;
            border: none;
            color: #9ca3af;
            font-size: 1rem;
            font-weight: bold;
            cursor: pointer;
            text-decoration: underline;
            transition: color 0.2s;
        }
        .btn-skip:hover { color: #4b5563; }

        @media (max-width: 600px) {
            .header { padding: 16px 20px 8px; }
            .header h2 { font-size: 1.3rem; }
            .header p { font-size: 0.9rem; margin-top: 6px; }
            .cards { grid-template-columns: 1fr; padding: 10px 20px; gap: 12px; }
            .card { padding: 12px 16px; flex-direction: row; text-align: left; align-items: center; gap: 12px;}
            .icon-circle { width: 40px; height: 40px; font-size: 18px; margin-bottom: 0; flex-shrink: 0;}
            .card h3 { font-size: 1.1rem; margin-bottom: 4px; }
            .card p { font-size: 0.8rem; }
            .footer { padding: 12px 20px 16px; }
        }
    `;

    static properties = {
        visible: { type: Boolean, reflect: true },
        aiCodingEnabled: { type: Boolean, state: true } // 追記: AI機能の有効状態プロパティ
    };

    constructor() {
        super();
        this.visible = false;

        // ストアから初期状態を取得
        this.aiCodingEnabled = userSettingStore.getState().userSetting.aiCodingEnabled;

        // ストアの購読
        this.unsubscribeUserSetting = userSettingStore.subscribe(() => {
            this.aiCodingEnabled = userSettingStore.getState().userSetting.aiCodingEnabled;
        });
    }

    connectedCallback() {
        super.connectedCallback();

        // モーダル上での操作が背後のキャンバス等に伝播しないようイベントをこの位置で停止
        this._preventPropagation = (e) => {
            e.stopPropagation();
        };

        this.addEventListener('touchstart', this._preventPropagation, { passive: true });
        this.addEventListener('touchmove', this._preventPropagation, { passive: true });
        this.addEventListener('touchend', this._preventPropagation, { passive: true });
        this.addEventListener('mousedown', this._preventPropagation);
        this.addEventListener('mousemove', this._preventPropagation);
        this.addEventListener('mouseup', this._preventPropagation);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._preventPropagation) {
            this.removeEventListener('touchstart', this._preventPropagation);
            this.removeEventListener('touchmove', this._preventPropagation);
            this.removeEventListener('touchend', this._preventPropagation);
            this.removeEventListener('mousedown', this._preventPropagation);
            this.removeEventListener('mousemove', this._preventPropagation);
            this.removeEventListener('mouseup', this._preventPropagation);
        }
    }

    firstUpdated() {
        requestAnimationFrame(() => {
            const fontAwesomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
            if (fontAwesomeStyle) {
                const fontAwesomeStyleClone = fontAwesomeStyle.cloneNode(true);
                fontAwesomeStyleClone.removeAttribute('disabled');
                this.shadowRoot.insertBefore(fontAwesomeStyleClone, this.shadowRoot.firstChild);
            }
        });
    }

    show() {
        this.visible = true;
    }

    hide() {
        this.visible = false;
        this.dispatchEvent(new CustomEvent('starter-modal-closed', { bubbles: true, composed: true }));
    }

    _select(type) {
        if (type === 'ai') {
            window.dispatchEvent(new CustomEvent('open-ai-agent-window', { bubbles: true, composed: true }));
        } else {
            window.dispatchEvent(new CustomEvent('apply-starter-template', { detail: { type }, bubbles: true, composed: true }));
        }
        // 非表示にする前に、選択されたタイプを載せたイベントを発行します
        this.visible = false;
        this.dispatchEvent(new CustomEvent('starter-modal-closed', {
            detail: { selectedType: type },
            bubbles: true,
            composed: true
        }));
    }

    render() {
        if (!this.visible) return html``;

        return html`
            <div class="overlay">
                <div class="modal">
                    <div class="header">
                        <h2>アプリの骨組みを選んでください</h2>
                        <p>作りたいアプリの形を選ぶと、すぐに開発をスタートできます。</p>
                    </div>
                    <div class="cards">
                        <div class="card" @click=${() => this._select('navigator')}>
                            <div class="icon-circle"><i class="fa-solid fa-mobile-screen"></i></div>
                            <div>
                                <h3>基本の画面遷移</h3>
                                <p>一般的なページ移動のあるアプリを作成します。</p>
                            </div>
                        </div>
                        <div class="card" @click=${() => this._select('tabbar')}>
                            <div class="icon-circle"><i class="fa-solid fa-window-restore"></i></div>
                            <div>
                                <h3>タブメニュー</h3>
                                <p>画面下部に切り替えタブがあるアプリを作成します。</p>
                            </div>
                        </div>
                        <div class="card" @click=${() => this._select('splitter')}>
                            <div class="icon-circle"><i class="fa-solid fa-bars"></i></div>
                            <div>
                                <h3>サイドメニュー</h3>
                                <p>横から引き出すメニュー付きのアプリを作成します。</p>
                            </div>
                        </div>
                        
                        <!-- aiCodingEnabled が true の場合のみレンダリング -->
                        ${this.aiCodingEnabled ? html`
                        <div class="card ai-card" @click=${() => this._select('ai')}>
                            <div class="icon-circle"><i class="fa-solid fa-wand-magic-sparkles"></i></div>
                            <div>
                                <h3>AIに作ってもらう</h3>
                                <p>チャットで作りたいものを伝えて自動生成します。</p>
                            </div>
                        </div>
                        ` : ''}
                    </div>
                    <div class="footer">
                        <button class="btn-skip" @click=${this.hide}>閉じて一から自分で作る（スキップ）</button>
                    </div>
                </div>
            </div>
        `;
    }
}
customElements.define('starter-template-modal', StarterTemplateModal);